import { AiMessageRoleEnum, AiMessageStatusEnum } from 'graphql-common';
import { isAiStreamTerminalStatus } from '@lib/utils/ai/streamReducer';
import isAiToolActivityMessage from './isAiToolActivityMessage';

type OutcomeCandidate = {
  id: string;
  role: AiMessageRoleEnum;
  content?: string | null;
  status?: AiMessageStatusEnum | null;
  toolCalls?: { id: string }[] | null;
};

// Whether the newest transcript row is the current turn's outcome — an answer,
// or the blank failure marker the BE keeps when a turn ends before any text
// streams (datizy-api/docs/fe/ai_frontend_guide.md §5). Either way the turn is
// over and the persisted row renders it, so the live turn state can be dropped.
//
// `turnStartNewestId` is the newest row from BEFORE the turn started, and is
// what scopes this to the CURRENT turn: a transcript that has not caught up yet
// still ends with the PREVIOUS turn's answer, which is also terminal and
// otherwise indistinguishable. Pass undefined only when there is genuinely
// nothing to compare against — a new conversation (no transcript), or a turn
// already running on load (safe: a running turn has its user message persisted,
// so the newest row is not an assistant row).
//
// Tool-activity rows are excluded: a turn ending on tools has more coming.
export default function isAiTurnOutcomeRow(
  newest: OutcomeCandidate | undefined,
  turnStartNewestId?: string,
): newest is OutcomeCandidate {
  if (!newest) return false;
  if (newest.id === turnStartNewestId) return false;
  if (newest.role !== AiMessageRoleEnum.Assistant) return false;
  if (isAiToolActivityMessage(newest)) return false;
  return isAiStreamTerminalStatus(newest.status);
}
